// src/lib/api.ts
import axios, { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { useAuthStore } from '../store/authStore';
import type {
  PortfolioLearning,
  PortfolioRules,
  PortfolioSignals,
  PortfolioSummary,
  PortfolioTrade,
  PortfolioTradeInput,
  Alert,
  ApiError,
  PaginatedResponse,
  TriggeredAlert,
  TermsOfService,
  TermsStatus,
  TermsAcceptResponse,
  DashboardStats,
  BuyingIndexData,
  TopInsider,
  CompanyActivityLeader,
  InsiderActivityLeader,
  ActivityLeadersResponse,
  UserSubscription,
  UserProfile,
  SectorFlowData,
  InterestingFindingsData,
  InsiderType,
  InstitutionalComparison,
  NotablesPayload,
  CompanyFinancials,
  InvestorDetail,
  InvestorHolder,
  InvestorRankingsParams,
  InvestorRankingsPayload,
  InvestorSearchResult,
  InvestorSummary,
  LookThroughResponse,
  OverlapPayload,
} from '../types/api';
import { API_CONFIG, SENTRY_CONFIG } from './config';
import { CircuitBreaker, CircuitBreakerOpenError } from './resilience';
import { notify } from '../store/notificationStore';
import logger from './logger';

// Circuit breaker for the primary API backend.  Rejects calls immediately
// after 5 consecutive failures and probes again after 60 s.
const apiCircuitBreaker = new CircuitBreaker({
  name: 'backend-api',
  failureThreshold: 5,
  recoveryTimeoutMs: 60_000,
});

type InvitationAccessMode = 'trial' | 'timed_premium';

// Function to create a standardized error object
const createSafeError = (error: unknown): ApiError => {
  // Narrow to AxiosError for response access
  const axiosErr = error instanceof AxiosError ? error : null;

  if (
    !axiosErr &&
    typeof error === 'object' &&
    error !== null &&
    ('status' in error || 'error' in error)
  ) {
    const existingError = error as ApiError;
    if (existingError.error?.message || existingError.message || existingError.status) {
      return existingError;
    }
  }

  const responseData = axiosErr?.response?.data as Record<string, unknown> | undefined;
  let message = 'An unexpected error occurred. Please try again.';
  let code = 'UNKNOWN_ERROR';
  let details = {};

  if (typeof responseData === 'object' && responseData !== null) {
    if (responseData.detail) {
      message = String(responseData.detail);
    } else if (responseData.non_field_errors) {
      const nfe = responseData.non_field_errors;
      message = Array.isArray(nfe) ? String(nfe[0]) : String(nfe);
    } else if (Object.keys(responseData).length > 0) {
      const firstKey = Object.keys(responseData)[0];
      const firstVal = responseData[firstKey];
      if (Array.isArray(firstVal)) {
        message = `${firstKey}: ${String(firstVal[0])}`;
      } else {
        message = `${firstKey}: ${String(firstVal)}`;
      }
      details = responseData;
    }
    if (responseData.code) {
      code = String(responseData.code);
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return {
    error: { code: String(responseData?.code || code), message: message, details: details },
    status: axiosErr?.response?.status,
    message: message,
  };
};

// --- Axios Instance ---
const api = axios.create({
  ...API_CONFIG,
  withCredentials: false,
});

const isHandledClientError = (status?: number): boolean =>
  Boolean(status && status >= 400 && status < 500 && status !== 429);

const shouldCaptureApiError = (error: AxiosError): boolean => {
  if (!SENTRY_CONFIG.enabled) {
    return false;
  }

  if (error instanceof CircuitBreakerOpenError) {
    return true;
  }

  const status = error.response?.status;
  if (!status) {
    return true;
  }

  if (status >= 500 || status === 429) {
    return true;
  }

  return SENTRY_CONFIG.captureHandledApiErrors && isHandledClientError(status);
};

// --- Token Refresh Logic ---
let isRefreshing = false;
// Requests that hit a 401 while a refresh is already in flight park a
// callback here. Each callback receives the new access token on success, or
// null if the refresh failed so the queued request can reject instead of
// hanging forever.
let refreshSubscribers: ((token: string | null) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string | null) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshComplete = (token: string | null) => {
  const subscribers = refreshSubscribers;
  refreshSubscribers = [];
  subscribers.forEach((cb) => cb(token));
};

const refreshAccessToken = async (): Promise<string> => {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) {
    console.error('Refresh token missing, logging out.');
    useAuthStore.getState().logout();
    throw new Error('No refresh token available');
  }
  try {
    const refreshResponse = await axios.post(
      `${API_CONFIG.baseURL}/token/refresh/`,
      { refresh: refreshToken },
      { headers: API_CONFIG.headers }
    );
    // The backend rotates refresh tokens (ROTATE_REFRESH_TOKENS +
    // BLACKLIST_AFTER_ROTATION), so the response carries a fresh refresh
    // token that must replace the old one — reusing the old token would fail
    // the next refresh because it is now blacklisted.
    const { access, refresh: rotatedRefresh } = refreshResponse.data;
    await useAuthStore.getState().setTokens({ access, refresh: rotatedRefresh || refreshToken });
    logger.log('Token refreshed successfully.');
    return access;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    useAuthStore.getState().logout();
    throw createSafeError(error);
  }
};
// --- End Token Refresh Logic ---

// --- Request Interceptor ---
api.interceptors.request.use(
  (config) => {
    // Reject immediately if the circuit is open (backend unreachable)
    const cbState = apiCircuitBreaker.getState();
    if (cbState === 'open') {
      return Promise.reject(new CircuitBreakerOpenError('backend-api')) as never;
    }

    const { accessToken } = useAuthStore.getState();
    const isRefreshRequest = config.url?.endsWith('/token/refresh/');

    if (accessToken && config.headers && !isRefreshRequest) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(createSafeError(error));
  }
);

// --- Response Interceptor ---
api.interceptors.response.use(
  (response) => {
    apiCircuitBreaker.recordSuccess();
    return response;
  },
  async (error: AxiosError) => {
    // Track server / network errors in the circuit breaker (skip 4xx)
    const status = error.response?.status;
    if (!status || status >= 500) {
      apiCircuitBreaker.recordFailure();
    }
    const originalRequest = error.config;

    // --- THIS IS THE CRITICAL FIX ---
    const responseData = error.response?.data as Record<string, unknown>;
    // Check for our specific structured error from the alert test endpoint.
    if (responseData && Array.isArray(responseData.execution_log)) {
      // If it's our special error, bypass the generic error flattener
      // and reject with the raw data so the component can handle it.
      return Promise.reject(error.response?.data);
    }
    // --- END CRITICAL FIX ---

    // Skip token refresh for login endpoint - let login errors pass through
    const isLoginEndpoint = originalRequest?.url?.endsWith('/token/');

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest.url?.endsWith('/token/refresh/') &&
      !isLoginEndpoint &&
      !(originalRequest as Record<string, unknown>)._retry
    ) {
      (originalRequest as Record<string, unknown>)._retry = true;
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          // Release any requests queued behind this refresh.
          onRefreshComplete(newToken);
          return api(originalRequest);
        } catch (refreshError) {
          // Unblock queued requests so they reject instead of hanging.
          onRefreshComplete(null);
          return Promise.reject(
            createSafeError(refreshError || new Error('Session expired. Please login again.'))
          );
        } finally {
          isRefreshing = false;
        }
      } else {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            if (token) {
              resolve(api(originalRequest));
            } else {
              reject(createSafeError(new Error('Session expired. Please login again.')));
            }
          });
        });
      }
    }

    // Handle network errors
    if (!error.response) {
      notify.error(
        'Network Error',
        'Unable to connect to the server. Please check your internet connection.'
      );
    } else if (error.response.status >= 500) {
      // Don't show error notifications for terms endpoints - they have their own error handling
      const isTermsEndpoint = originalRequest?.url?.includes('/terms/');
      if (!isTermsEndpoint) {
        notify.error('Server Error', 'Something went wrong on our end. Please try again later.');
      }
    } else if (error.response.status === 403) {
      // Check if it's a subscription-related 403
      const errorData = error.response.data as Record<string, unknown>;
      const message = String(errorData?.detail || '');

      if (
        errorData?.code === 'premium_required' ||
        message.toLowerCase().includes('premium subscriber') ||
        message.toLowerCase().includes('upgrade') ||
        message.toLowerCase().includes('subscription')
      ) {
        // This is a subscription-related error - trigger upgrade prompt
        notify.error('Premium Feature', message || 'This feature requires a premium subscription.');
        // Dispatch a custom event that components can listen to
        window.dispatchEvent(
          new CustomEvent('subscription-required', {
            detail: { message, feature: 'unknown' },
          })
        );
      } else {
        notify.error('Access Denied', 'You do not have permission to perform this action.');
      }
    } else if (error.response.status === 404) {
      // Don't show notification for 404s as they're often handled by the UI
    } else if (error.response.status === 400) {
      // Show specific error message if available
      const errorData = error.response.data as Record<string, unknown>;
      if (errorData?.detail) {
        notify.error('Request Error', String(errorData.detail));
      }
    }

    // Capture only operationally useful API failures in Sentry by default.
    if (shouldCaptureApiError(error) && error.response?.status !== 401) {
      const captureStatus = error.response?.status;
      const handledClientError = isHandledClientError(captureStatus);
      const errorKind =
        error instanceof CircuitBreakerOpenError
          ? 'circuit_breaker'
          : !captureStatus
            ? 'network'
            : handledClientError
              ? 'handled_client'
              : captureStatus >= 500
                ? 'server'
                : 'rate_limit';

      Sentry.withScope((scope) => {
        scope.setTag('http_status', String(captureStatus ?? 'network_error'));
        scope.setTag('http_method', String(originalRequest?.method?.toUpperCase() ?? 'UNKNOWN'));
        scope.setTag('api_url', String(originalRequest?.url ?? 'unknown'));
        scope.setTag('signal_surface', 'api');
        scope.setTag('error_kind', errorKind);
        scope.setTag('alert_actionability', handledClientError ? 'informational' : 'actionable');
        scope.setContext('api_error', {
          url: originalRequest?.url,
          method: originalRequest?.method,
          status: captureStatus,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
        });
        Sentry.captureException(error);
      });
    }

    // All other errors get processed by our generic handler.
    return Promise.reject(createSafeError(error));
  }
);
// --- End Response Interceptor ---

// --- Auth API Function Definitions ---
export const auth = {
  login: async (
    username: string,
    password: string
  ): Promise<{ access: string; refresh: string }> => {
    try {
      const response = await api.post<{ access: string; refresh: string }>('/token/', {
        username,
        password,
      });
      await useAuthStore
        .getState()
        .login({ access: response.data.access, refresh: response.data.refresh });
      notify.success('Welcome back!', 'You have successfully logged in.');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  logout: () => {
    useAuthStore.getState().logout();
    notify.info('Logged out', 'You have been successfully logged out.');
  },
  register: async (userData: Record<string, unknown>): Promise<unknown> => {
    const response = await api.post('/register/', userData);
    notify.success(
      'Account created!',
      'Your account has been successfully created. Please log in.'
    );
    return response.data;
  },
};
// --- End Auth API Functions ---

// --- Onboarding API Functions ---
export const onboardingApi = {
  update: async (data: {
    onboarding_completed?: boolean;
    onboarding_dismissed?: boolean;
  }): Promise<UserProfile> => {
    try {
      const response = await api.patch<UserProfile>('/users/me/onboarding/', data);
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
};
// --- End Onboarding API Functions ---

// --- Alert API Function Definitions ---
export const alertsApi = {
  list: async (): Promise<Alert[]> => {
    try {
      const response = await api.get('/alerts/'); // Remove <PaginatedResponse<Alert>>—let TS infer

      let alerts: Alert[] = [];

      // Normalize response shape
      const data = response.data;
      if (Array.isArray(data)) {
        // Flat array (post-pagination disable)
        alerts = data;
      } else if (data && Array.isArray(data.results)) {
        // Paginated shape (old/legacy)
        alerts = data.results;
      } else {
        // Fallback: Treat as array or empty
        alerts = data ? [data] : [];
      }

      // Debug log (remove after testing)
      logger.log(`Fetched ${alerts.length} alerts`);

      return alerts;
    } catch (error) {
      console.error('Failed to fetch alerts:', error); // Add for debugging
      throw error;
    }
  },
  create: async (
    alertData: Partial<
      Omit<
        Alert,
        | 'id'
        | 'user'
        | 'username'
        | 'created_at'
        | 'updated_at'
        | 'last_checked_at'
        | 'last_triggered_at'
      >
    >
  ): Promise<Alert> => {
    const response = await api.post<Alert>('/alerts/', alertData);
    return response.data;
  },
  retrieve: async (id: string): Promise<Alert> => {
    const response = await api.get<Alert>(`/alerts/${id}/`);
    return response.data;
  },
  update: async (
    id: string,
    alertData: Partial<
      Omit<
        Alert,
        | 'id'
        | 'user'
        | 'username'
        | 'created_at'
        | 'updated_at'
        | 'last_checked_at'
        | 'last_triggered_at'
      >
    >
  ): Promise<Alert> => {
    const response = await api.patch<Alert>(`/alerts/${id}/`, alertData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/alerts/${id}/`);
  },
  getTriggeredAlerts: async (
    page: number = 1,
    pageSize: number = 25,
    ordering?: string
  ): Promise<PaginatedResponse<TriggeredAlert>> => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });

    if (ordering) {
      params.append('ordering', ordering);
    }

    const response = await api.get<PaginatedResponse<TriggeredAlert>>(
      `/alerts/triggered/?${params.toString()}`
    );
    return response.data;
  },
};
// --- End Alert API Functions ---

// --- Stripe API Functions ---
export const stripeApi = {
  createSetupIntent: async () => {
    try {
      const response = await api.post('/subscriptions/create-setup-intent/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  createCheckoutSession: async (data: {
    planId?: string;
    priceId?: string;
    successUrl: string;
    cancelUrl: string;
  }) => {
    try {
      const response = await api.post('/subscriptions/create-checkout-session/', {
        plan_id: data.planId,
        success_url: data.successUrl,
        cancel_url: data.cancelUrl,
      });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  createPortalSession: async (data: { returnUrl: string }) => {
    try {
      const response = await api.post('/subscriptions/create-portal-session/', {
        return_url: data.returnUrl,
      });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  getSubscription: async () => {
    try {
      const response = await api.get('/subscriptions/subscription/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  cancelSubscription: async () => {
    try {
      const response = await api.post('/subscriptions/cancel-subscription/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  resumeSubscription: async () => {
    try {
      const response = await api.post('/subscriptions/resume-subscription/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  getStripeConfig: async () => {
    try {
      const response = await api.get('/subscriptions/stripe-config/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
};
// --- End Stripe API Functions ---

// --- Invitation API Functions ---
export const invitationApi = {
  checkRequired: async (email: string): Promise<{ invitation_required: boolean }> => {
    try {
      const response = await api.post('/subscriptions/invitations/check-required/', { email });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  validateCode: async (
    code: string,
    email?: string
  ): Promise<{
    valid: boolean;
    message?: string;
    trial_days?: number;
    access_mode?: InvitationAccessMode;
  }> => {
    try {
      const response = await api.post('/subscriptions/invitations/validate/', { code, email });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  redeemCode: async (
    code: string,
    email?: string
  ): Promise<{ success: boolean; message: string; access_mode?: InvitationAccessMode }> => {
    try {
      const response = await api.post('/subscriptions/invitations/redeem/', { code, email });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  redeemForExistingUser: async (
    code: string
  ): Promise<{
    success: boolean;
    message: string;
    access_mode?: InvitationAccessMode;
    subscription?: UserSubscription;
  }> => {
    try {
      const response = await api.post('/subscriptions/invitations/redeem-existing/', { code });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
};
// --- End Invitation API Functions ---

// --- Terms API Functions ---
export const termsApi = {
  getCurrent: async (): Promise<TermsOfService> => {
    try {
      const response = await api.get<TermsOfService>('/terms/current/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  getStatus: async (): Promise<TermsStatus> => {
    try {
      const response = await api.get<TermsStatus>('/terms/status/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  accept: async (): Promise<TermsAcceptResponse> => {
    try {
      const response = await api.post<TermsAcceptResponse>('/terms/accept/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
};
// --- End Terms API Functions ---

// --- Stats API Functions ---
export const statsApi = {
  /**
   * Get dashboard summary stats
   * @param insiderType - Filter by insider type: 'all', 'human', or 'institutional'
   */
  getSummary: async (insiderType?: InsiderType, days?: number): Promise<DashboardStats> => {
    try {
      const params: Record<string, string | number> = {};
      if (insiderType) params.insider_type = insiderType;
      if (days) params.days = days;
      const response = await api.get<DashboardStats>('/stats/summary/', {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  /**
   * Get sector flow data for dashboard
   * @param insiderType - Filter by insider type: 'all', 'human', or 'institutional'
   */
  getSectorFlow: async (insiderType?: InsiderType, days?: number): Promise<SectorFlowData[]> => {
    try {
      const params: Record<string, string | number> = {};
      if (insiderType) params.insider_type = insiderType;
      if (days) params.days = days;
      const response = await api.get<SectorFlowData[]>('/stats/sector-flow/', {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  /**
   * Get the trailing 365-day insider buying index.
   * Uses reported open-market Form 4 purchases (code P) only.
   */
  getBuyingIndex: async (insiderType?: InsiderType): Promise<BuyingIndexData> => {
    try {
      const params: Record<string, string> = {};
      if (insiderType) params.insider_type = insiderType;
      const response = await api.get<BuyingIndexData>('/stats/buying-index/', {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  /**
   * Get interesting market findings/patterns
   * @param days - Analysis period in days
   * @param insiderType - Filter by insider type: 'all', 'human', or 'institutional'
   */
  getFindings: async (
    days: number = 90,
    insiderType?: InsiderType
  ): Promise<InterestingFindingsData> => {
    try {
      const params: Record<string, string | number> = { days };
      if (insiderType) {
        params.insider_type = insiderType;
      }
      const response = await api.get<InterestingFindingsData>('/stats/findings/', { params });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  /**
   * Get institutional comparison data (human vs institutional side-by-side)
   * @param days - Analysis period in days (default 30)
   */
  getInstitutionalComparison: async (days: number = 30): Promise<InstitutionalComparison> => {
    try {
      const response = await api.get<InstitutionalComparison>('/stats/institutional-comparison/', {
        params: { days },
      });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  /**
   * Get most-active companies for the selected recent window.
   */
  getMostActiveCompanies: async (
    days: 30 | 60 | 90 = 30,
    insiderType: InsiderType = 'all',
    limit: number = 20,
    ordering?: string | null
  ): Promise<ActivityLeadersResponse<CompanyActivityLeader>> => {
    try {
      const params: Record<string, string | number> = {
        days,
        insider_type: insiderType,
        limit,
      };
      if (ordering) {
        params.ordering = ordering;
      }
      const response = await api.get<ActivityLeadersResponse<CompanyActivityLeader>>(
        '/stats/most-active-companies/',
        { params }
      );
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  /**
   * Get most-active insiders for the selected recent window.
   */
  getMostActiveInsiders: async (
    days: 30 | 60 | 90 = 30,
    insiderType: InsiderType = 'all',
    limit: number = 20,
    ordering?: string | null
  ): Promise<ActivityLeadersResponse<InsiderActivityLeader>> => {
    try {
      const params: Record<string, string | number> = {
        days,
        insider_type: insiderType,
        limit,
      };
      if (ordering) {
        params.ordering = ordering;
      }
      const response = await api.get<ActivityLeadersResponse<InsiderActivityLeader>>(
        '/stats/most-active-insiders/',
        { params }
      );
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },

  /**
   * Get high conviction transactions for the signal feed
   * Uses the existing transactions endpoint with conviction filter
   */
  /**
   * Get top performing insiders
   * Uses the existing insiders endpoint with skill score sorting
   */
  getTopInsiders: async (limit: number = 5, days?: number): Promise<TopInsider[]> => {
    try {
      const params: Record<string, string | number> = {
        ordering: '-insider_skill_score',
        page_size: limit,
      };
      if (days) params.days = days;
      const response = await api.get<PaginatedResponse<TopInsider>>('/insiders/', { params });
      return response.data.results;
    } catch (error) {
      throw createSafeError(error);
    }
  },
};
// --- End Stats API Functions ---

export const companyFinancialsApi = {
  /**
   * Financial snapshot for a company (premium). The first request for a
   * company fetches its statements, so it can take a second or two.
   */
  get: async (companyLookup: string | number): Promise<CompanyFinancials> => {
    try {
      const response = await api.get<CompanyFinancials>(
        `/companies/${encodeURIComponent(String(companyLookup))}/financials/`
      );
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
};

export const notablesApi = {
  /**
   * Get the curated Notables screens (premium). Served from the nightly
   * dashboard-stats cache with a live-compute fallback.
   */
  getNotables: async (): Promise<NotablesPayload> => {
    try {
      const response = await api.get<NotablesPayload>('/notables/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
};

export interface InvestorFollowPrefs {
  notify_new_filing?: boolean;
  notify_insider_buys?: boolean;
}

export const investorsApi = {
  list: async (q?: string): Promise<InvestorSummary[]> => {
    try {
      const response = await api.get<{ results: InvestorSummary[] }>('/investors/', {
        params: q ? { q } : undefined,
      });
      return response.data.results;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  rankings: async (params: InvestorRankingsParams = {}): Promise<InvestorRankingsPayload> => {
    try {
      const response = await api.get<InvestorRankingsPayload>('/investors/rankings/', { params });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  searchEdgar: async (q: string): Promise<InvestorSearchResult[]> => {
    try {
      const response = await api.get<{ results: InvestorSearchResult[] }>('/investors/search/', {
        params: { q },
      });
      return response.data.results;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  add: async (cik: number): Promise<InvestorSummary> => {
    try {
      const response = await api.post<InvestorSummary>('/investors/', { cik }, { timeout: 120000 });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  get: async (cik: number, period?: string): Promise<InvestorDetail> => {
    try {
      const response = await api.get<InvestorDetail>(`/investors/${cik}/`, {
        params: period ? { period } : undefined,
      });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  follow: async (cik: number, prefs: InvestorFollowPrefs = {}): Promise<InvestorSummary> => {
    try {
      const response = await api.post<InvestorSummary>(`/investors/${cik}/follow/`, prefs);
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  unfollow: async (cik: number): Promise<void> => {
    try {
      await api.delete(`/investors/${cik}/follow/`);
    } catch (error) {
      throw createSafeError(error);
    }
  },
  overlap: async (skilledOnly = false): Promise<OverlapPayload> => {
    try {
      const response = await api.get<OverlapPayload>('/investors/overlap/', {
        params: skilledOnly ? { skilled_only: 1 } : undefined,
      });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  lookThrough: async (cik: number): Promise<LookThroughResponse> => {
    try {
      const response = await api.get<LookThroughResponse>(`/investors/${cik}/lookthrough/`, {
        timeout: 120000,
      });
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  heldBy: async (ticker: string): Promise<InvestorHolder[]> => {
    try {
      const response = await api.get<{ results: InvestorHolder[] }>(
        `/investors/held-by/${encodeURIComponent(ticker)}/`
      );
      return response.data.results;
    } catch (error) {
      throw createSafeError(error);
    }
  },
};

// Default export the configured axios instance
export const portfolioApi = {
  summary: async (): Promise<PortfolioSummary> => {
    try {
      const response = await api.get<PortfolioSummary>('/portfolio/summary/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  trades: async (): Promise<PortfolioTrade[]> => {
    try {
      const response = await api.get<{ results: PortfolioTrade[] }>('/portfolio/trades/');
      return response.data.results;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  addTrade: async (trade: PortfolioTradeInput): Promise<PortfolioTrade> => {
    try {
      const response = await api.post<PortfolioTrade>('/portfolio/trades/', trade);
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  deleteTrade: async (id: number): Promise<void> => {
    try {
      await api.delete(`/portfolio/trades/${id}/`);
    } catch (error) {
      throw createSafeError(error);
    }
  },
  rules: async (): Promise<PortfolioRules> => {
    try {
      const response = await api.get<PortfolioRules>('/portfolio/rules/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  saveRules: async (rules: PortfolioRules): Promise<PortfolioRules> => {
    try {
      const response = await api.put<PortfolioRules>('/portfolio/rules/', rules);
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  signals: async (): Promise<PortfolioSignals> => {
    try {
      const response = await api.get<PortfolioSignals>('/portfolio/signals/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
  learning: async (): Promise<PortfolioLearning> => {
    try {
      const response = await api.get<PortfolioLearning>('/portfolio/learning/');
      return response.data;
    } catch (error) {
      throw createSafeError(error);
    }
  },
};

export default api;
